import { historyPush } from '../../../../common/utils/historyHelper';
import { Navigator } from '../../../effects/navigator';
import { ContentLibActions } from '../../../store/contentLib/actions';
import { ContentLibSelectors } from '../../../store/contentLib/selectors';
import { EContentLibViewMode } from '../../../store/contentLib/types';
import { EMainViewMode } from '../../../store/general/types';
import { AppThunk } from '../../../store/types';
import { IContentLibOptions } from '../../../types/common';
import { getAuthUser, getMainViewMode } from '../../../store/general/selectors';
import { setAuthUserAction, setMainViewMode, setPublicPageAction } from '../../../store/general/actions';
import { AuthManager } from '../../../../common/utils/auth-manager';
import { findLanguageByCode, getTargetLanguage } from '../../../store/models/selectors';
import { LangUtil } from '../../../../common/utils/lang-util';
import { restoreAppSettings } from '../../../effects/prepareAfterOpen';
import { updateUserLanguages } from '../../../effects/updateUserLanguages';
import { updateUserGroups } from '../../../effects/updateUserGroups';
import { PhraseDetailsTabEffects } from '../../../effects/phraseDetailsTabEffects';
import { VideoTutorialEffects } from '../../../effects/videoTutorialEffects';
import { setUserGroupsLoadedAction } from '../../../store/models/actions';
import { getUserStateStorage } from '../../../../common/utils/local-storage/user-state';
import { ContentLibEffects } from '../../../effects/contentLibEffects';
import { AuthUserEffects } from '../../../effects/authUserEffects';
import { PlayListLevelTitles } from '../PlayList/PlayListLevelSelect/types';

export class ContentLibEvents {
    public static openGroup(groupId: number): AppThunk {
        return (
          dispatch,
          getState
        ) => {
          const viewMode = ContentLibSelectors.getViewMode(getState());
          dispatch(ContentLibActions.setSelectedGroupId(groupId));
          dispatch(ContentLibActions.setViewMode(EContentLibViewMode.GROUP_VIDEO_LIST));

          historyPush({
            state: { page: 'Playlist' },
            title: 'Playlist',
            onpopstate: () => dispatch(this.closeGroup(groupId, viewMode)),
          });
        }
    }

    public static closeGroup(
      groupId: number,
      viewMode: EContentLibViewMode,
    ): AppThunk {
      return (
        dispatch,
        getState
      ) => {
        if (getMainViewMode(getState()) === EMainViewMode.CONTENT_LIBRARY) {
          Navigator.openContentLib();
        }
        dispatch(ContentLibActions.setViewMode(viewMode));
        setTimeout(() => {
          const group = document.querySelector(`.content-lib-group-${groupId}`) as HTMLElement;
          if (group) {
            group.scrollIntoView({ block: 'center', behavior: 'smooth'});
          }
        }, 300);
      }
  }

  public static load(
    mainViewMode?: EMainViewMode,
    options?: IContentLibOptions,
  ): AppThunk {
    return async (
      dispatch,
      getState
    ) => {
      if (!getAuthUser(getState())) { // url
        dispatch(setPublicPageAction(true));
        let result = await AuthManager.autoLogin();
        if (!result?.authUser && mainViewMode === EMainViewMode.MY_LIBRARY) {
          return Navigator.openContentLib();
        }
        if (!result?.authUser) {
          const targetLangCode = getTargetLanguage(getState())?.code || 'en';
          let nativeLangCode = LangUtil.checkLangCode(navigator.language);
          if (nativeLangCode === targetLangCode) {
            nativeLangCode = targetLangCode === 'en' ? 'fr' : 'en';
          }
          result = await AuthManager.loadAnonymous(targetLangCode, nativeLangCode);
        }
        if (result && result.authUser) {
          restoreAppSettings(result.appSettings);
          dispatch(setAuthUserAction(result.authUser));
          dispatch(updateUserLanguages());
          dispatch(updateUserGroups(true));
          PhraseDetailsTabEffects.load();
          VideoTutorialEffects.loadList();
        }
        dispatch(setUserGroupsLoadedAction(true));
        dispatch(setMainViewMode(mainViewMode || EMainViewMode.CONTENT_LIBRARY));
      } else {
        const mainViewModeState = getMainViewMode(getState());
        if (mainViewModeState !== EMainViewMode.CONTENT_LIBRARY &&
          mainViewModeState !== EMainViewMode.MY_LIBRARY
        ) {
          dispatch(setMainViewMode(mainViewMode || EMainViewMode.CONTENT_LIBRARY));
        }
        if (mainViewMode === EMainViewMode.CONTENT_LIBRARY) {
          getUserStateStorage().setContentLibPage();
        } else if (mainViewMode === EMainViewMode.MY_LIBRARY) {
          getUserStateStorage().setMyStuffPage();
        }
      }

      await ContentLibEffects.loadLangs();
      await ContentLibEffects.loadCategories();

      if (findLanguageByCode(getState(), options?.langCode)) {
        AuthUserEffects.setLang(options?.langCode);
      }

      if (options?.groupId) {
        if (ContentLibSelectors.getSelectedGroupId(getState()) !== +options.groupId) {
          await ContentLibEffects.searchGroups();
          dispatch(ContentLibEvents.openGroup(+options.groupId));
        }
      } else {
        let category;
        let level;
        [options?.option1, options?.option2].forEach(option => {
          const categories = ContentLibSelectors.getCategories(getState());
          const categoryNames = categories
            .map(item => {
              let arr = item.name.split(' ');
              return arr[0].toLowerCase();
            });
          if (categoryNames.includes(option)) {
            category = categories.find(item => item.name.toLocaleLowerCase().includes(option));
            if (category) {
              dispatch(ContentLibActions.setSelectedCategoryId(category.id));
              dispatch(ContentLibActions.setOpenCategories(true));
              return;
            }
          }

          const levels = Object.values(PlayListLevelTitles)
            .map(item => {
              return item.toLowerCase();
            });
          if (levels.includes(option)) {
            level = Object.entries(PlayListLevelTitles).find(([key, value]) => value.toLocaleLowerCase().includes(option));
            if (level) dispatch(ContentLibActions.setSearchFilter({ level: +level[0] }));
          }
        });

        if (!category && ContentLibSelectors.getSelectedCategoryId(getState())) { // browser back
          dispatch(ContentLibActions.setSelectedCategoryId(null as any));
          dispatch(ContentLibActions.setOpenCategories(false));
        }
        if (!level && ContentLibSelectors.getSearchFilter(getState())?.level) { // browser back
          dispatch(ContentLibActions.setSearchFilter({ level: null as any }));
        }
      }
    }
  }
}